import { useState } from "react";
import { Loader2, ListChecks, Check } from "lucide-react";
import adminApi from "../lib/adminApi";
import { Button } from "./ui/button";
import { usd as money } from "../lib/money";

// Dry-run first, apply only after the owner confirms: print-order credits for orders that were paid before the top-up existed.
export const CreditBackfill = ({ enabled, onApplied }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const run = async (apply) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/user-spend/credits/backfill", { apply });
      if (apply) {
        setDone(data);
        setPreview(null);
        onApplied?.();
      } else {
        setPreview(data);
        setDone(null);
      }
    } catch {
      setError("The backfill could not run. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-dashed border-border px-4 py-3" data-testid="credit-backfill">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold">Backfill credits for existing paid orders</p>
        <Button data-testid="credit-backfill-preview" size="sm" variant="outline" className="rounded-full h-9 gap-1.5" disabled={busy || !enabled} onClick={() => run(false)}>
          {busy && !preview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />} Preview (dry run)
        </Button>
      </div>
      {!enabled && <p className="mt-1 text-xs text-muted-foreground">Set USER_SPEND_ORDER_CREDIT_USD above 0 first — with the top-up off there is nothing to backfill.</p>}
      {preview && (
        <div className="mt-2 text-xs font-mono2" data-testid="credit-backfill-result">
          <p data-testid="credit-backfill-total">
            Would grant {money(preview.total_usd)} to {preview.would_grant.length} user{preview.would_grant.length === 1 ? "" : "s"} · {preview.unmatched.length} unmatched · {preview.skipped.length} skipped
          </p>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            {preview.would_grant.map((r) => (
              <li key={r.user_id} data-testid={`credit-backfill-user-${r.user_id}`}>
                {r.email || r.user_id} · +{money(r.amount_usd)} · {r.orders.map((o) => o.order_name).join(", ")}
              </li>
            ))}
            {preview.unmatched.map((o) => (
              <li key={`u-${o.order_name}-${o.duck_id}`}>unmatched · {o.order_name} · {o.duck_name || o.duck_id}</li>
            ))}
          </ul>
          {preview.would_grant.length > 0 && (
            <Button data-testid="credit-backfill-apply" size="sm" className="mt-2 rounded-full h-9 gap-1.5 font-bold" disabled={busy} onClick={() => run(true)}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Apply — grant {money(preview.total_usd)} now
            </Button>
          )}
        </div>
      )}
      {done && (
        <p className="mt-2 text-xs font-mono2 text-emerald-700" data-testid="credit-backfill-done">
          Applied: {done.granted.length} credit{done.granted.length === 1 ? "" : "s"} granted ({money(done.total_usd)}). Running it again grants nothing more.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-rose-700" data-testid="credit-backfill-error">{error}</p>}
    </div>
  );
};
